import HomeModuleCard from './HomeModuleCard';
import { HOME_MODULES } from './homeModuleConfig';

const SEGMENT_ACTIVE =
  'border-cyan-500/80 bg-cyan-950/40 text-cyan-300 shadow-[0_0_16px_rgba(6,182,212,0.25)] ring-1 ring-cyan-400/50';
const SEGMENT_IDLE =
  'border-slate-800/80 bg-slate-900/40 text-slate-400 hover:border-slate-700 hover:text-slate-200';

/**
 * Intelligence module selector — 2×2 on phone/tablet (md), 1×4 from lg (desktop).
 * Idle: no module highlighted until click or auto-detect.
 *
 * @param {{
 *   activeModuleId: string | null,
 *   onSelectModule: (id: string) => void,
 *   collapsed?: boolean,
 *   dense?: boolean,
 * }} props
 */
export default function HomeModuleGrid({
  activeModuleId,
  onSelectModule,
  collapsed = false,
  dense = false,
}) {
  return (
    <div
      className={
        collapsed
          ? 'pointer-events-none max-h-0 scale-95 overflow-hidden opacity-0 transition-all duration-300 ease-out'
          : 'max-h-[220px] scale-100 overflow-visible opacity-100 transition-all duration-300 delay-100 ease-out'
      }
      aria-hidden={collapsed || undefined}
      data-testid={collapsed ? 'home-module-grid-collapsed' : 'home-module-grid-shell'}
    >
      <nav
        className="home-module-segmented mt-2 grid w-full grid-cols-2 gap-2 md:hidden"
        aria-label="Intelligence modules"
        data-testid="home-module-segmented"
      >
        {HOME_MODULES.map((m) => {
          const isActive = Boolean(activeModuleId) && activeModuleId === m.id;
          const Icon = m.Icon;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onSelectModule(m.id)}
              aria-pressed={isActive}
              aria-label={m.title}
              title={m.pattern}
              tabIndex={collapsed ? -1 : undefined}
              className={`inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border px-3 py-2 transition active:scale-[0.98] ${
                isActive ? SEGMENT_ACTIVE : SEGMENT_IDLE
              }`}
              data-testid={`home-module-seg-${m.id}`}
            >
              <Icon
                className={`h-4 w-4 shrink-0 ${isActive ? 'text-cyan-300' : 'opacity-90'}`}
                aria-hidden
                strokeWidth={2.25}
              />
              <span
                className={`text-[11px] font-bold uppercase leading-none tracking-wide ${
                  isActive ? 'text-cyan-300' : 'text-inherit'
                }`}
              >
                {m.mobileLabel}
              </span>
            </button>
          );
        })}
      </nav>

      <section
        className={`home-module-grid-compact mt-3 hidden w-full grid-cols-2 gap-3.5 transition-all duration-500 ease-out md:mt-3 md:grid lg:grid-cols-4 ${
          dense ? 'home-module-grid-has-results' : ''
        }`}
        aria-label="Intelligence modules"
        data-testid="home-module-grid"
      >
        {HOME_MODULES.map((m) => (
          <HomeModuleCard
            key={m.id}
            title={m.title}
            short={m.short}
            pattern={m.pattern}
            help={m.help}
            Icon={m.Icon}
            accent={m.accent}
            isActive={Boolean(activeModuleId) && activeModuleId === m.id}
            onSelect={() => onSelectModule(m.id)}
          />
        ))}
      </section>
    </div>
  );
}
